import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Badge,
} from '@mui/material'
import {
  Chat as ChatIcon,
  Favorite as FavoriteIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material'
import { Profile, GENDER_LABELS } from '@/types/profile'
import { ProfileImageAvatar } from '@/components/ProfileImageAvatar'
import {
  ROOM_OWNER_DEFAULT_NAME,
  ROOM_OWNER_ROLE_LABEL,
  ROOM_OWNER_DESCRIPTION,
} from '@/utils/roomOwner'

export interface ProfileListCardProps {
  profile: Profile
  isMobile: boolean
  /** プレビューモード: ボタンを表示するが操作不可 */
  preview?: boolean
  onChatClick?: (profile: Profile) => void
  onProfileClick?: (profile: Profile) => void
  unreadCount?: number
}

// タイプ別のアクセントカラー
const typeColor: Record<Profile['type'], string> = {
  business: '#6366f1',
  dating: '#ec4899',
  job: '#059669',
}

export const ProfileListCard = ({
  profile,
  isMobile,
  preview = false,
  onChatClick,
  onProfileClick,
  unreadCount = 0,
}: ProfileListCardProps) => {
  const accentColor = typeColor[profile.type]

  // ── ヘッダー部分（タイプ別） ──────────────────────────
  const renderCardHeader = () => {
    // 主催者でプロフィール未登録の場合、タイプ別の空欄チップが並ぶのを避け、
    // 「管理者」であることが分かる表示にする
    if (profile.isRoomOwner && !hasTypeDetails()) {
      return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight={700} noWrap>
            {profile.name || ROOM_OWNER_DEFAULT_NAME}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {ROOM_OWNER_ROLE_LABEL}
          </Typography>
        </Box>
      )
    }
    if (profile.type === 'business') {
      return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight={700} noWrap>
            {profile.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {profile.companyName}
          </Typography>
          <Chip label={profile.position} size="small" sx={{ mt: 0.5, fontSize: '0.7rem' }} />
        </Box>
      )
    }
    if (profile.type === 'dating') {
      return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight={700}>
              {profile.name}
            </Typography>
            <Chip
              label={`No.${profile.userNumber}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {profile.age}歳 / {GENDER_LABELS[profile.gender]}
          </Typography>
          <Typography variant="body2" color="text.secondary">{profile.occupation}</Typography>
        </Box>
      )
    }
    // job
    return (
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant={isMobile ? 'body1' : 'h6'} fontWeight={700} noWrap>
          {profile.name}
        </Typography>
        {profile.companyName && (
          <Typography variant="body2" color="text.secondary" noWrap>{profile.companyName}</Typography>
        )}
        <Chip
          icon={<WorkIcon sx={{ fontSize: '0.9rem !important' }} />}
          label={profile.lastPosition}
          size="small"
          sx={{ mt: 0.5, fontSize: '0.7rem' }}
        />
      </Box>
    )
  }

  // タイプ別の主要項目が入力されているか
  const hasTypeDetails = (): boolean => {
    if (profile.type === 'business') return Boolean(profile.companyName || profile.businessContent)
    if (profile.type === 'dating') return Boolean(profile.occupation || profile.age)
    return Boolean(profile.lastPosition || profile.education)
  }

  // ── タイプ別のメインコンテンツ ────────────────────────
  const renderTypeContent = () => {
    // 主催者はプロフィール未登録の場合があり、タイプ別項目が空欄で並ぶと
    // 情報が無いように見える。問い合わせ先である旨を明示する。
    if (profile.isRoomOwner && !profile.pr && !hasTypeDetails()) {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {ROOM_OWNER_DESCRIPTION}
          </Typography>
        </Box>
      )
    }

    if (profile.type === 'business') {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block">ビジネス内容</Typography>
          <Typography variant="body2">{profile.businessContent}</Typography>
        </Box>
      )
    }
    if (profile.type === 'dating') {
      return (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">年収</Typography>
            <Typography variant="body2" fontWeight={500}>{profile.annualIncome}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">子供</Typography>
            <Typography variant="body2" fontWeight={500}>{profile.wantChildren || '未設定'}</Typography>
          </Grid>
        </Grid>
      )
    }
    // job
    return (
      <>
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">最終学歴</Typography>
            <Typography variant="body2" fontWeight={500}>{profile.education}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">学校名</Typography>
            <Typography variant="body2" fontWeight={500} noWrap>{profile.schoolName}</Typography>
          </Grid>
        </Grid>
        {profile.age && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              年齢: {profile.age}歳{profile.gender && ` / ${GENDER_LABELS[profile.gender]}`}
            </Typography>
          </Box>
        )}
      </>
    )
  }

  // ── チャットボタンのスタイル ──────────────────────────
  const chatButtonIcon = profile.type === 'dating' ? <FavoriteIcon /> : <ChatIcon />

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'all 0.2s ease-in-out',
        ...(profile.isRoomOwner
          ? { border: '2px solid', borderColor: 'warning.main' }
          : {}),
        '&:hover': {
          transform: isMobile || preview ? 'none' : 'translateY(-4px)',
          boxShadow: preview ? 1 : 4,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        {/* 主催者バッジ：問い合わせ先として一目で分かるようにする */}
        {profile.isRoomOwner && (
          <Chip
            icon={<AdminIcon sx={{ fontSize: '0.9rem !important' }} />}
            label="主催者"
            size="small"
            color="warning"
            sx={{ mb: 1.5, fontWeight: 700, fontSize: '0.7rem' }}
          />
        )}

        {/* アバター + ヘッダー */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <ProfileImageAvatar
            userId={profile.id}
            type={profile.type}
            fallbackSrc={profile.photoUrl}
            sx={{ width: { xs: 56, sm: 72 }, height: { xs: 56, sm: 72 } }}
          />
          {renderCardHeader()}
        </Box>

        {/* タイプ別コンテンツ */}
        {renderTypeContent()}

        {/* PR（共通） */}
        {profile.pr && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">PR</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
              {profile.pr}
            </Typography>
          </Box>
        )}

        {/* マッチしたい相手 */}
        {profile.wantToMatch && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {profile.type === 'dating' ? 'マッチしたい相手' : 'マッチしたい業種'}
            </Typography>
            <Typography variant="body2" color="primary.main" sx={{ fontSize: '0.8rem' }}>
              {profile.wantToMatch}
            </Typography>
          </Box>
        )}

        {/* アクションボタン */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PersonIcon />}
            size={isMobile ? 'small' : 'medium'}
            disabled={preview}
            onClick={() => onProfileClick?.(profile)}
            sx={{ flex: 1, borderColor: accentColor, color: accentColor }}
          >
            プロフィール
          </Button>
          <Badge
            badgeContent={unreadCount}
            color="error"
            sx={{ flex: 1, '& .MuiBadge-badge': { top: -4, right: 8 } }}
          >
            <Button
              variant="contained"
              startIcon={chatButtonIcon}
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              disabled={preview}
              onClick={() => onChatClick?.(profile)}
              sx={{ bgcolor: accentColor, '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' } }}
            >
              チャット
            </Button>
          </Badge>
        </Box>
      </CardContent>
    </Card>
  )
}
